/**
 * login-passwordless-sms-otp/index.tsx
 * Auth0 ACUL screen: "login-passwordless-sms-otp"
 *
 * WHAT THIS SCREEN DOES
 * ---------------------
 * Collects the 6-digit OTP code sent via SMS to the user's phone number.
 * Reached when Auth0 routes to this screen after a /authorize?connection=sms
 * request.
 *
 * The user can:
 *   - Enter the 6-digit code and click "Log in"
 *   - Resend the OTP (30-second cooldown)
 *   - Switch to password login (see TODO)
 *   - Go back to the identifier screen
 *
 * ALTERNATE METHOD
 * ----------------
 * "Use password instead" is shown for tenants that support phone as a
 * database connection username.  TODO: confirm with Auth0 support that
 * your tenant's DB connection accepts E.164 phone numbers as usernames.
 *
 * HOW TO EXTEND
 * -------------
 * The `handleSubmitOTP` call requires a `username` field — the hook reads
 * it from `screen.data.phoneNumber`.  If not available, it falls from
 * sessionStorage via `loadIdentifier()`.
 */

import { AuthLayout } from "@/components/auth/AuthLayout";
import { OtpForm } from "@/components/auth/OtpForm";
import { loadIdentifier } from "@/utils/navigation";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";
import { useLoginPasswordlessSmsOtpManager } from "./hooks/useLoginPasswordlessSmsOtpManager";

function LoginPasswordlessSmsOtpScreen() {
  const {
    loginPasswordlessSmsOtp,
    texts,
    locales,
    data,
    handleSubmitOTP,
    handleResendOTP,
    handleSwitchToPasswordless,
  } = useLoginPasswordlessSmsOtpManager();

  document.title =
    texts?.pageTitle || locales?.page?.title || "Check your phone";
  applyAuth0Theme(loginPasswordlessSmsOtp);

  const phoneTarget =
    ((data as Record<string, unknown> | undefined)?.phoneNumber as string) ??
    (loadIdentifier() || "your mobile number");

  const phone = loadIdentifier();

  const handleSubmit = (code: string) => {
    handleSubmitOTP({ username: phone, code });
  };

  const handleResend = () => {
    handleResendOTP();
  };

  const handleBack = () => {
    window.history.back();
  };

  // TODO: wire this to a redirect that starts a DB-connection password flow
  const handleSwitchToPassword = () => {
    handleSwitchToPasswordless();
  };

  return (
    <AuthLayout title="Check your phone">
      <OtpForm
        target={phoneTarget}
        onSubmit={handleSubmit}
        onResend={handleResend}
        onBack={handleBack}
        alternateLabel="Use password instead"
        onAlternate={handleSwitchToPassword}
        submitLabel="Log in"
      />
    </AuthLayout>
  );
}

export default LoginPasswordlessSmsOtpScreen;
